import { observer } from 'mobx-react-lite'

import { Scrollbar, Status, Typography } from 'shared/ui'
import { ModalInfoBlockWithTitle } from 'shared/ui/Modal/ui/ModalInfoBlockWithTitle/ModalInfoBlockWithTitle'

import { uiStore } from 'shared/store/uiStore'
import { DayjsFormats } from 'shared/lib'
import { type IResponseExternalWebhook, type IWebhookHistoryRow } from 'entities/Webhook'
import { WebhookStatus } from 'pages/settings/pages/integrations/pages/Webhooks/ui/WebhookStatus/WebhookStatus'
import styles from './styles.module.scss'

type IHistoryDetailsModalContentProps = {
  webhook: IResponseExternalWebhook | null
  history: IWebhookHistoryRow
}

export const HistoryDetailsModalContent = observer(
  ({ history, webhook }: IHistoryDetailsModalContentProps) => {
    const isFiled = history.fields.status === 'failed'

    const baseLines = [
      {
        label: 'Request URL',
        value: history.fields.requestUrl,
      },
      {
        label: 'Request parameters',
        value: (
          <div className={styles.stringDataContainer}>
            {JSON.stringify(history.fields.requestParams)}
          </div>
        ),
      },
      {
        label: 'Status',
        value: (
          <Status
            emphasis='high'
            intent={isFiled ? 'negative' : 'positive'}
            title={isFiled ? 'Failed' : 'Success'}
            icon={isFiled ? 'closeCircle' : 'check2'}
          />
        ),
      },
      {
        label: 'Response code',
        value: history.fields.responseCode,
      },
      {
        label: 'Response body',
        value: <div className={styles.stringDataContainer}>{history.fields.responseBody}</div>,
      },
    ]

    const errorLines = isFiled
      ? [
          {
            label: 'Error',
            value: history.fields.exceptionInfo,
          },
          {
            label: 'Retry count',
            value: history.fields.retryCount,
          },
        ]
      : []

    const lines = [
      ...baseLines,
      ...errorLines,
      {
        label: 'Created at',
        value: uiStore.dayjs(history.fields.createdAt).format(DayjsFormats.fullWithAtDash),
      },
    ]

    if (!webhook) return null

    return (
      <div className={styles.wrap}>
        <div className={styles.titleWrap}>
          <Typography
            tag={'div'}
            ariaLabel={'detailsWebhookName'}
            variant={'heading-lg'}
            tooltipProps={{
              label: webhook.name,
            }}
            ellipsis
          >
            {webhook.name}
          </Typography>
          <WebhookStatus isActive={webhook?.is_active} />
        </div>

        <Scrollbar>
          <ModalInfoBlockWithTitle title={history.fields.eventKey} lines={lines} />
        </Scrollbar>
      </div>
    )
  }
)
